import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import {
  fetchFinanceSettings,
  updateFinanceSettings,
  fetchFeeAdjustmentTypes,
  createFeeAdjustmentType,
  updateFeeAdjustmentType,
  deleteFeeAdjustmentType,
} from '../../store/slices/financeSlice'
import type { FinanceSettingsInput, FeeAdjustmentTypeInput } from '@/types/finance'
import { useToast } from '../../components/ToastContainer'
import usePermissions, { Permission } from '../../hooks/usePermissions'
import { FinanceAuditLog } from '../../components/finance'
import {
  Cog6ToothIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ArrowLeftIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'

const FinanceSettings = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()
  const { currentProject } = useAppSelector(state => state.project)
  const {
    settings,
    settingsLoading,
    settingsError,
    feeAdjustmentTypes,
    feeAdjustmentTypesLoading,
  } = useAppSelector(state => state.finance)

  const { hasPermission } = usePermissions(currentProject?.id)
  const canView = hasPermission(Permission.VIEW_FINANCE_SETTINGS)
  const canManage = hasPermission(Permission.MANAGE_FINANCE_SETTINGS)

  const [settingsForm, setSettingsForm] = useState<FinanceSettingsInput>({})
  const [isEditingSettings, setIsEditingSettings] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)

  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false)
  const [isEditingAdjustment, setIsEditingAdjustment] = useState(false)
  const [editingAdjustmentId, setEditingAdjustmentId] = useState<string | null>(null)
  const [adjustmentForm, setAdjustmentForm] = useState<FeeAdjustmentTypeInput>({
    name: '',
    defaultAmount: 0,
    isActive: true,
  })
  const [savingAdjustment, setSavingAdjustment] = useState(false)

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(fetchFinanceSettings(currentProject.id))
      dispatch(fetchFeeAdjustmentTypes(currentProject.id))
    }
  }, [dispatch, currentProject?.id])

  useEffect(() => {
    if (settings) {
      setSettingsForm({
        communityName: settings.communityName || '',
        taxId: settings.taxId || '',
        feePerPing: settings.feePerPing,
        carParkingFee: settings.carParkingFee,
        motorParkingFee: settings.motorParkingFee,
        bikeParkingFee: settings.bikeParkingFee,
        paperBillTemplate: settings.paperBillTemplate,
        emailBillTemplate: settings.emailBillTemplate,
        achUploadTemplate: settings.achUploadTemplate,
      })
    }
  }, [settings])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const handleSaveSettings = async () => {
    if (!currentProject?.id) return

    setSavingSettings(true)
    try {
      await dispatch(updateFinanceSettings({
        projectId: currentProject.id,
        data: settingsForm,
      })).unwrap()
      showSuccess('財務設定已更新')
      setIsEditingSettings(false)
    } catch (error: any) {
      showError('儲存失敗', error.message || '請稍後再試')
    } finally {
      setSavingSettings(false)
    }
  }

  const openCreateAdjustmentModal = () => {
    setAdjustmentForm({ name: '', defaultAmount: 0, isActive: true })
    setIsEditingAdjustment(false)
    setEditingAdjustmentId(null)
    setShowAdjustmentModal(true)
  }

  const openEditAdjustmentModal = (type: any) => {
    setAdjustmentForm({
      name: type.name,
      defaultAmount: type.defaultAmount,
      isActive: type.isActive,
    })
    setIsEditingAdjustment(true)
    setEditingAdjustmentId(type.id)
    setShowAdjustmentModal(true)
  }

  const closeAdjustmentModal = () => {
    setShowAdjustmentModal(false)
    setEditingAdjustmentId(null)
    setAdjustmentForm({ name: '', defaultAmount: 0, isActive: true })
  }

  const handleSaveAdjustment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentProject?.id) return

    if (!adjustmentForm.name.trim()) {
      showError('請輸入名稱')
      return
    }

    setSavingAdjustment(true)
    try {
      if (isEditingAdjustment && editingAdjustmentId) {
        await dispatch(updateFeeAdjustmentType({
          projectId: currentProject.id,
          typeId: editingAdjustmentId,
          data: adjustmentForm,
        })).unwrap()
        showSuccess('調整類型已更新')
      } else {
        await dispatch(createFeeAdjustmentType({
          projectId: currentProject.id,
          data: adjustmentForm,
        })).unwrap()
        showSuccess('調整類型已建立')
      }
      closeAdjustmentModal()
    } catch (error: any) {
      showError('儲存失敗', error.message || '請稍後再試')
    } finally {
      setSavingAdjustment(false)
    }
  }

  const handleDeleteAdjustment = async (type: any) => {
    if (!currentProject?.id) return
    if (type.isPreset) {
      showError('無法刪除', '預設類型無法刪除')
      return
    }
    if (!confirm(`確定要刪除「${type.name}」嗎？`)) return

    try {
      await dispatch(deleteFeeAdjustmentType({
        projectId: currentProject.id,
        typeId: type.id,
      })).unwrap()
      showSuccess('調整類型已刪除')
    } catch (error: any) {
      showError('刪除失敗', error.message || '請稍後再試')
    }
  }

  if (settingsLoading && !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto mt-12 text-center bg-white rounded-lg shadow">
        <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">無權限存取</h2>
        <p className="mt-2 text-sm text-gray-600">您沒有檢視財務設定的權限，請聯絡專案管理員。</p>
        <Link to={`/project/${projectKey}/finance`} className="inline-block mt-4 text-blue-600 hover:underline">回財務管理首頁</Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/project/${projectKey}/finance`}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <Cog6ToothIcon className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">財務設定</h1>
              <p className="text-sm text-gray-500">管理費率與系統設定</p>
            </div>
          </div>
        </div>
      </div>

      {settingsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{settingsError}</p>
        </div>
      )}

      {/* Basic Settings */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">基本設定</h2>
          {!isEditingSettings ? (
            canManage ? (
              <button
                onClick={() => setIsEditingSettings(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                編輯
              </button>
            ) : null
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setIsEditingSettings(false)
                  if (settings) {
                    setSettingsForm({
                      communityName: settings.communityName || '',
                      taxId: settings.taxId || '',
                      feePerPing: settings.feePerPing,
                      carParkingFee: settings.carParkingFee,
                      motorParkingFee: settings.motorParkingFee,
                      bikeParkingFee: settings.bikeParkingFee,
                    })
                  }
                }}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <CheckIcon className="w-4 h-4 mr-2" />
                {savingSettings ? '儲存中...' : '儲存'}
              </button>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                社區名稱
              </label>
              {isEditingSettings ? (
                <input
                  type="text"
                  value={settingsForm.communityName || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, communityName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="XX社區管理委員會"
                />
              ) : (
                <div className="text-gray-900">{settings?.communityName || '-'}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                統一編號
              </label>
              {isEditingSettings ? (
                <input
                  type="text"
                  value={settingsForm.taxId || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, taxId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="12345678"
                  maxLength={8}
                />
              ) : (
                <div className="text-gray-900">{settings?.taxId || '-'}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fee Rates */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">費率設定</h2>
          {!isEditingSettings ? (
            canManage ? (
              <button
                onClick={() => setIsEditingSettings(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                編輯
              </button>
            ) : null
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setIsEditingSettings(false)
                  if (settings) {
                    setSettingsForm({
                      communityName: settings.communityName || '',
                      taxId: settings.taxId || '',
                      feePerPing: settings.feePerPing,
                      carParkingFee: settings.carParkingFee,
                      motorParkingFee: settings.motorParkingFee,
                      bikeParkingFee: settings.bikeParkingFee,
                      paperBillTemplate: settings.paperBillTemplate,
                      emailBillTemplate: settings.emailBillTemplate,
                      achUploadTemplate: settings.achUploadTemplate,
                    })
                  }
                }}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <CheckIcon className="w-4 h-4 mr-2" />
                {savingSettings ? '儲存中...' : '儲存'}
              </button>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                每坪管理費
              </label>
              {isEditingSettings ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={settingsForm.feePerPing || 0}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, feePerPing: parseFloat(e.target.value) || 0 }))}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <div className="text-xl font-semibold text-gray-900">
                  {formatCurrency(settings?.feePerPing || 0)}/坪
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                汽車位月費
              </label>
              {isEditingSettings ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={settingsForm.carParkingFee || 0}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, carParkingFee: parseFloat(e.target.value) || 0 }))}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <div className="text-xl font-semibold text-blue-600">
                  {formatCurrency(settings?.carParkingFee || 0)}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                機車位月費
              </label>
              {isEditingSettings ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={settingsForm.motorParkingFee || 0}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, motorParkingFee: parseFloat(e.target.value) || 0 }))}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <div className="text-xl font-semibold text-green-600">
                  {formatCurrency(settings?.motorParkingFee || 0)}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                腳踏車位月費
              </label>
              {isEditingSettings ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={settingsForm.bikeParkingFee || 0}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, bikeParkingFee: parseFloat(e.target.value) || 0 }))}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <div className="text-xl font-semibold text-yellow-600">
                  {formatCurrency(settings?.bikeParkingFee || 0)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fee Adjustment Types */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">費用調整類型</h2>
          {canManage && (
            <button
              onClick={openCreateAdjustmentModal}
              className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              新增類型
            </button>
          )}
        </div>

        <div className="p-6">
          {feeAdjustmentTypesLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : feeAdjustmentTypes.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              尚無費用調整類型
            </div>
          ) : (
            <div className="space-y-3">
              {feeAdjustmentTypes.map((type) => (
                <div
                  key={type.id}
                  className={`flex items-center justify-between p-4 border rounded-lg ${
                    type.isActive ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="font-medium text-gray-900">
                        {type.name}
                        {type.isPreset && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            系統預設
                          </span>
                        )}
                        {!type.isActive && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            已停用
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        預設金額: {formatCurrency(type.defaultAmount)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {canManage && (
                      <button
                        onClick={() => openEditAdjustmentModal(type)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="編輯"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                    {canManage && !type.isPreset && (
                      <button
                        onClick={() => handleDeleteAdjustment(type)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="刪除"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 財務設定操作紀錄 */}
      <FinanceAuditLog section="settings" title="設定操作紀錄" limit={50} />

      {/* Fee Adjustment Type Modal */}
      {showAdjustmentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditingAdjustment ? '編輯費用調整類型' : '新增費用調整類型'}
              </h3>
              <button
                onClick={closeAdjustmentModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSaveAdjustment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={adjustmentForm.name}
                  onChange={(e) => setAdjustmentForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例如: 空調費、網路費"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  預設金額
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={adjustmentForm.defaultAmount}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, defaultAmount: parseFloat(e.target.value) || 0 }))}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  正數為加收，負數為減免
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={adjustmentForm.isActive !== false}
                  onChange={(e) => setAdjustmentForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  啟用此調整類型
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAdjustmentModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={savingAdjustment}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingAdjustment ? '儲存中...' : '儲存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default FinanceSettings
